import { Fragment, type ReactElement } from "react";

import {
  NotFound,
  TableHeaderCustom,
  TableOver,
  TablePagination,
} from "@/components/atoms";
import { ExpandableRow } from "@/components/molecules";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { PaginatedResponse } from "@/types/api";
import type { IGroupParams } from "@/utils/hooks/useTableParams";
import { isNotLastItem } from "@/utils/isNotLastItem";

import type { ColumnConfig } from "./type";

type DynamicTableProps<T> = {
  data: T[];
  columns: ColumnConfig<T>[];
  isPagination?: boolean;
  tableClasses?: string;
  children?: ReactElement | ReactElement[];
  next?: () => void;
  prev?: () => void;
  searchValue?: string;
  tableParams?: PaginatedResponse<T>;
  setTableParams?: React.Dispatch<React.SetStateAction<IGroupParams>>;
  isExpandable?: boolean;
  notFoundTitle?: string;
  notFoundClassName?: string;
};

const DynamicTable = <T,>({
  data,
  columns,
  isPagination = true,
  tableClasses,
  children,
  next,
  prev,
  searchValue,
  tableParams,
  setTableParams,
  isExpandable = false,
  notFoundTitle,
  notFoundClassName,
}: DynamicTableProps<T>) => {
  const visibleColumns = columns.filter(col => col.visible);
  const hasFooter = visibleColumns.some(col => col.footerRenderer);

  return (
    <>
      {children}

      <TableOver
        classes={cn(
          tableClasses,
          data?.length === 0 && "border-b-0 rounded-bl-none rounded-br-none"
        )}
      >
        <TableHeaderCustom>
          <TableRow>
            {visibleColumns.map(col => (
              <col.headerComponent key={String(col.key)} label={col.label} />
            ))}
          </TableRow>
        </TableHeaderCustom>

        {data?.length > 0 ? (
          <TableBody>
            {isExpandable
              ? data.map((row, index) => (
                  <ExpandableRow
                    key={row.id ?? index}
                    row={row}
                    columns={columns}
                    data={data}
                  />
                ))
              : data.map((row, index) => {
                  const isLast = isNotLastItem(index, data);
                  return (
                    <TableRow key={row.id ?? index}>
                      {visibleColumns.map(col => (
                        <Fragment key={String(col.key)}>
                          {col.cellRenderer(row, isLast)}
                        </Fragment>
                      ))}
                    </TableRow>
                  );
                })}
          </TableBody>
        ) : (
          <></>
        )}

        {data?.length > 0 && hasFooter ? (
          <TableFooter>
            <TableRow>
              {visibleColumns.map(col => (
                <TableCell
                  key={String(col.key)}
                  className="px-4 py-4 text-xs font-semibold"
                >
                  {col.footerRenderer?.()}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        ) : (
          <></>
        )}
      </TableOver>

      {data?.length === 0 && (
        <NotFound
          value={searchValue ?? ""}
          title={notFoundTitle}
          className={notFoundClassName}
        />
      )}

      {isPagination && data?.length > 0 && (
        <TablePagination
          next={next}
          prev={prev}
          limit={tableParams?.limit}
          offset={tableParams?.offset}
          total={tableParams?.total ?? 0}
          setLimit={val =>
            setTableParams?.(prev => ({ ...prev, limit: val, offset: 0 }))
          }
        />
      )}
    </>
  );
};

export default DynamicTable;
